import React, { FC } from 'react'
import {
  Variant,
  VariantSelector,
  PriceVariantSelector,
  isDigitalGiftCard,
  formatPriceWithoutDecimals,
} from '@moonpig/web-shared-products'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { useLanguage } from '@moonpig/web-core-stores'
import { useProductContext } from '../../../contexts/product'
import { useVariantSelectorModuleLocaleText } from './VariantSelectorModule.locale'
import { calculatePriceDifference } from './calculatePriceDifference'
import { getVariantDescription } from './getVariantDescription'
import { getSelectorTitle } from './getSelectorTitle'
import { ProductDetailsStateActionTypes } from '../../../contexts/product/types'
import { ProductModule, VariantType } from '../types'
import { ProductModuleSection } from '../ProductModuleSection'

export const VariantSelectorModule: FC<ProductModule> = props => {
  const [state, dispatch] = useProductContext()
  const { product, selectedOptions } = state
  const language = useLanguage()
  const localiseText = useVariantSelectorModuleLocaleText()
  const giftCardVariantsEnabled = useExperiment('gift-card-variants', {
    fallback: false,
  })

  const { variants, isLandscape, category } = product

  if (variants.length === 1) return null

  if (
    isDigitalGiftCard(product.category.department) &&
    !giftCardVariantsEnabled
  )
    return null

  const selectedVariant = variants.find(
    ({ key }) => key === selectedOptions.variantKey,
  ) as Variant

  const variantType = isDigitalGiftCard(product.category.department)
    ? VariantType.price
    : VariantType.size

  return (
    <ProductModuleSection {...props}>
      {variantType === VariantType.price ? (
        <PriceVariantSelector
          onChange={(variantSku: string) => {
            dispatch({
              type: ProductDetailsStateActionTypes.SET_SELECTED_VARIANT,
              payload: variantSku,
            })
          }}
          variants={variants
            .filter(v => v.sku !== null)
            .map(v => {
              return {
                sku: v.sku ?? /* istanbul ignore next */ '',
                price: formatPriceWithoutDecimals(
                  Math.abs(v.price.centAmount),
                  v.price.fractionDigits,
                  v.price.currencyCode,
                  1,
                  language,
                ),
              }
            })}
          selectedValue=""
        />
      ) : (
        <VariantSelector
          title={getSelectorTitle(category.id, localiseText)}
          isLandscape={!!isLandscape}
          selectedValue={selectedVariant.key}
          items={variants.map(variant => {
            return {
              label: variant.title,
              description: getVariantDescription(variant, localiseText),
              price: calculatePriceDifference(
                variant,
                selectedVariant,
                language,
              ),
              value: variant.key,
            }
          })}
          onChange={variantKey => {
            dispatch({
              type: ProductDetailsStateActionTypes.SET_SELECTED_VARIANT,
              payload: variantKey,
            })
          }}
        />
      )}
    </ProductModuleSection>
  )
}
